import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import * as api from '~/api';

export type Replica = {
  value: string;
  label: string;
};

export type Instance = {
  value: string;
  label: string;
};

type ReplicasState = {
  replicas: Replica[];
  instances: Instance[];
  canUpdateInstance: boolean;
};

type ReplicasActions = {
  setReplicas: (replicas: Replica[]) => void;
  setInstances: (instances: Instance[]) => void;
  setCanUpdateInstance: (canUpdate: boolean) => void;
  fetchReplicas: (realm: string) => Promise<void>;
  fetchInstances: (replicaName: string, realm: string) => Promise<void>;
  reset: () => void;
};

type ReplicasStore = ReplicasState & ReplicasActions;

const initialState: ReplicasState = {
  replicas: [],
  instances: [],
  canUpdateInstance: false
};

export const useReplicasStore = create<ReplicasStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setReplicas: replicas => set({ replicas }),

      setInstances: instances => set({ instances }),

      setCanUpdateInstance: canUpdateInstance => set({ canUpdateInstance }),

      fetchReplicas: async realm => {
        const { setReplicas } = get();

        const response = await api.connectors.getReplicas({ realm });
        const replicas = response?.data?.replicas;

        setReplicas(
          replicas.map(({ label }: { label: string }) => ({
            label,
            value: label
          }))
        );
      },

      fetchInstances: async (replicaName, realm) => {
        const { setInstances } = get();

        const { data: multiInstanceData } = await api.pipeline.multiInstance({
          replicaName,
          realm,
          environment: ''
        });

        setInstances(
          multiInstanceData.replicaInstance.map(
            ({ name }: { name: string }) => ({
              value: name,
              label: name
            })
          )
        );
      },

      reset: () => set({ ...initialState })
    })),
    { name: 'ReplicasStore' }
  )
);

// Standalone functions for use outside React components
export const fetchReplicas = (realm: string) =>
  useReplicasStore.getState().fetchReplicas(realm);

export const fetchInstances = (replicaName: string, realm: string) =>
  useReplicasStore.getState().fetchInstances(replicaName, realm);

export const setReplicas = (replicas: Replica[]) =>
  useReplicasStore.getState().setReplicas(replicas);

export const setInstances = (instances: Instance[]) =>
  useReplicasStore.getState().setInstances(instances);

export const setCanUpdateInstance = (canUpdate: boolean) =>
  useReplicasStore.getState().setCanUpdateInstance(canUpdate);

export const resetReplicasStore = () => useReplicasStore.getState().reset();
